import { useCallback, useEffect, useState } from "react";
import { Input, Modal, Spin, Tag, Typography } from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { startGetHistorialPorNumeroSerie } from "../../../../context/actions/crearServiciosV2Actions";
import "./HistorialNumeroSerieModal.css";

const { Text, Title } = Typography;
const { Search } = Input;

const normalizarHistorial = (data) => {
  if (Array.isArray(data)) return data;
  if (data) return [data];
  return [];
};

const formatearFecha = (fecha) => {
  if (!fecha) return "Sin fecha";

  const fechaDayjs = dayjs(fecha);
  return fechaDayjs.isValid() ? fechaDayjs.format("DD/MM/YYYY HH:mm") : fecha;
};

const getTagColor = (estatus = "") => {
  const estatusNormalizado = estatus.toLowerCase();

  if (estatusNormalizado.includes("terminada")) return "green";
  if (estatusNormalizado.includes("cancel")) return "red";
  if (estatusNormalizado.includes("abierto")) return "blue";
  return "default";
};

export const HistorialNumeroSerieModal = ({ open, numeroSerieInicial, onClose }) => {
  const dispatch = useDispatch();
  const [numeroSerie, setNumeroSerie] = useState("");
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [consultado, setConsultado] = useState(false);

  const consultarHistorial = useCallback(async (valorBusqueda) => {
    const busqueda = (valorBusqueda || "").trim();
    if (!busqueda) return;

    setLoading(true);
    const result = await dispatch(startGetHistorialPorNumeroSerie(busqueda));
    setHistorial(result.success ? normalizarHistorial(result.data) : []);
    setConsultado(true);
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;

    const busquedaInicial = (numeroSerieInicial || "").trim();
    setNumeroSerie(busquedaInicial);
    setHistorial([]);
    setConsultado(false);

    if (busquedaInicial) consultarHistorial(busquedaInicial);
  }, [consultarHistorial, open, numeroSerieInicial]);

  return (
    <Modal
      title={(
        <>
          <HistoryOutlined className="historial-numero-serie-modal__title-icon" />
          Historial de número de serie
        </>
      )}
      open={open}
      wrapClassName="historial-numero-serie-modal"
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <div className="historial-numero-serie-modal__intro">
        <Title level={3} className="historial-numero-serie-modal__heading">
          Consulta de historial
        </Title>
        <Text type="secondary" className="historial-numero-serie-modal__description">
          Busca un número de serie para revisar asistencias relacionadas y su estatus.
        </Text>
      </div>

      <Search
        className="historial-numero-serie-modal__search"
        value={numeroSerie}
        onChange={(event) => setNumeroSerie(event.target.value)}
        onSearch={consultarHistorial}
        placeholder="Número de serie"
        enterButton="Buscar"
        loading={loading}
        disabled={loading}
      />

      <div className="historial-numero-serie-history historial-numero-serie-history--modal">
        <Text className="historial-numero-serie-history__label">
          <HistoryOutlined /> Historial encontrado
        </Text>

        {loading ? (
          <div className="historial-numero-serie-history__loading"><Spin /></div>
        ) : !consultado ? (
          <div className="historial-numero-serie-history__empty">
            <Text className="historial-numero-serie-history__empty-title">Sin consulta activa</Text>
            <Text type="secondary" className="historial-numero-serie-history__empty-description">
              Ingresa un número de serie y presiona Buscar para consultar su historial.
            </Text>
          </div>
        ) : historial.length === 0 ? (
          <div className="historial-numero-serie-history__empty">
            <Text className="historial-numero-serie-history__empty-title">Sin registros encontrados</Text>
            <Text type="secondary" className="historial-numero-serie-history__empty-description">
              No hay asistencias asociadas al número de serie consultado.
            </Text>
          </div>
        ) : (
          historial.map((item, index) => (
            <div
              className="historial-numero-serie-history__item"
              key={`${item.asistenciaID || "historial"}-${item.fechaLlamadaIniciada || index}`}
            >
              <div className="historial-numero-serie-history__meta">
                <Text strong>Asistencia {item.asistenciaID || "sin ID"}</Text>
                {item.estatusNombre && (
                  <Tag color={getTagColor(item.estatusNombre)}>{item.estatusNombre}</Tag>
                )}
                {item.estatusFinalNombre && (
                  <Tag color={getTagColor(item.estatusFinalNombre)}>{item.estatusFinalNombre}</Tag>
                )}
                <Text type="secondary">{formatearFecha(item.fechaLlamadaIniciada)}</Text>
              </div>

              <div className="historial-numero-serie-history__detail-grid">
                <div className="historial-numero-serie-history__detail">
                  <span>Servicio</span>
                  <strong>{item.servicioNombre || "Sin servicio"}</strong>
                </div>
                <div className="historial-numero-serie-history__detail">
                  <span>Validador</span>
                  <strong>{item.validador1 || "Sin validador"}</strong>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
};
